import { RootEpic } from 'MyTypes';
import { from, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';

import {
    ReiTypeEtiology,
    ReiTypeImpairment,
    ReiTypeInjury,
    ReiTypeRisk,
} from '@portal/common/models/rei-types';
import { buildRaceHierarchy } from '@portal/common/utility/hierarchy-helpers';
import { applyLocationHierarchyWorkarounds } from '@portal/common/utility/location-helpers';

import config from '../../config';
import {
    getCauseIcdCodesAsync,
    getCausesAsync,
    getCrosAsync,
    getEtiologiesAsync,
    getImpairmentsAsync,
    getInjuryAsync,
    getLocationsAsync,
    getRacesAsync,
    getRisksAsync,
    getSequelasAsync,
    getVehssAsync,
} from './actions';

export const loadLocations: RootEpic = (action$, state$, { api }) =>
    action$.pipe(
        filter(isActionOf([getLocationsAsync.request])),
        switchMap(() =>
            from(api.getLocations(undefined)).pipe(
                map((res) => {
                    const modifiedRes = res.map((location) =>
                        applyLocationHierarchyWorkarounds(location, {
                            chinaLocationId: config.chinaLocationId,
                            australiaLocationId: config.australiaLocationId,
                            newZealandLocationId: config.newZealandLocationId,
                            southeastAsiaEastAsiaAndOceaniaLocationId:
                                config.southeastAsiaEastAsiaAndOceaniaLocationId,
                            oceaniaLocationId: config.oceaniaLocationId,
                        })
                    );

                    return getLocationsAsync.success(modifiedRes);
                }),
                catchError((message: string) => of(getLocationsAsync.failure(message)))
            )
        )
    );

export const loadCauses: RootEpic = (action$, state$, { api }) =>
    action$.pipe(
        filter(isActionOf([getCausesAsync.request])),
        switchMap(() =>
            from(api.data.getCauses()).pipe(
                map(getCausesAsync.success),
                catchError((message: string) => of(getCausesAsync.failure(message)))
            )
        )
    );

export const requestCauseIcdCodes: RootEpic = (action$) =>
    action$.pipe(
        filter(isActionOf([getCausesAsync.success])),
        map(() => getCauseIcdCodesAsync.request())
    );

export const loadCauseIcdCodes: RootEpic = (action$, state$, { api }) =>
    action$.pipe(
        filter(isActionOf([getCauseIcdCodesAsync.request])),
        switchMap(() =>
            from(api.data.getCauseIcdCodes()).pipe(
                map(getCauseIcdCodesAsync.success),
                catchError((message: string) => of(getCauseIcdCodesAsync.failure(message)))
            )
        )
    );

export const loadSequelas: RootEpic = (action$, state$, { api }) =>
    action$.pipe(
        filter(isActionOf([getSequelasAsync.request])),
        switchMap(() =>
            from(api.data.getSequelas()).pipe(
                map((res) => getSequelasAsync.success(res)),
                catchError((message: string) => of(getSequelasAsync.failure(message)))
            )
        )
    );

export const loadCros: RootEpic = (action$, state$, { api }) =>
    action$.pipe(
        filter(isActionOf([getCrosAsync.request])),
        switchMap(() =>
            from(api.data.getCros()).pipe(
                map((res) => getCrosAsync.success(res)),
                catchError((message: string) => of(getCrosAsync.failure(message)))
            )
        )
    );

export const loadRisks: RootEpic = (action$, state$, { api }) =>
    action$.pipe(
        filter(isActionOf([getRisksAsync.request])),
        switchMap(() =>
            from(api.data.getRei(ReiTypeRisk)).pipe(
                map(getRisksAsync.success),
                catchError((message: string) => of(getRisksAsync.failure(message)))
            )
        )
    );

export const loadEtiology: RootEpic = (action$, state$, { api }) =>
    action$.pipe(
        filter(isActionOf([getEtiologiesAsync.request])),
        switchMap(() =>
            from(api.data.getRei(ReiTypeEtiology)).pipe(
                map(getEtiologiesAsync.success),
                catchError((message: string) => of(getEtiologiesAsync.failure(message)))
            )
        )
    );

export const loadImpairment: RootEpic = (action$, state$, { api }) =>
    action$.pipe(
        filter(isActionOf([getImpairmentsAsync.request])),
        switchMap(() =>
            from(api.data.getRei(ReiTypeImpairment)).pipe(
                map(getImpairmentsAsync.success),
                catchError((message: string) => of(getImpairmentsAsync.failure(message)))
            )
        )
    );

export const loadInjury: RootEpic = (action$, state$, { api }) =>
    action$.pipe(
        filter(isActionOf([getInjuryAsync.request])),
        switchMap(() =>
            from(api.data.getRei(ReiTypeInjury)).pipe(
                map(getInjuryAsync.success),
                catchError((message: string) => of(getInjuryAsync.failure(message)))
            )
        )
    );

export const loadVehss: RootEpic = (action$, state$, { api }) =>
    action$.pipe(
        filter(isActionOf([getVehssAsync.request])),
        switchMap(() =>
            from(api.data.getVehss()).pipe(
                map(getVehssAsync.success),
                catchError((message: string) => of(getVehssAsync.failure(message)))
            )
        )
    );

export const loadRaces: RootEpic = (action$, state$, { api }) =>
    action$.pipe(
        filter(isActionOf([getRacesAsync.request])),
        switchMap(() =>
            from(api.data.getRaces()).pipe(
                map((res) => getRacesAsync.success(buildRaceHierarchy(res))),
                catchError((message: string) => of(getRacesAsync.failure(message)))
            )
        )
    );
